import React from "react";
import {
  BrowserRouter as Router,
  Redirect,
  Route,
  Switch,
} from "react-router-dom";
import AdminLayout from "./layouts/Admin.js";
import CodCashReceiptDialog from "./components/CodCashReceiptDialog.js";
import ReceiptPrintDialog from "./components/Orders/ReceiptPrintDialog.js";
import AuthLayout from "./layouts/Auth.js";
import PrivateRoute from "./PrivateRoute.js";
import VendorRegistration from "./views/Forms/VendorRegistration.js";
import AgentPasswordResetRequestScreen from "./views/Forms/AgentPasswordResetRequestScreen.js";
import VendorRegistrationForm from "./views/Forms/VendorRegistrationForm.js";
import AgentPasswordResetForm from "./views/Forms/AgentPasswordResetForm.js";
import MobileClientPasswordResetForm from "./views/Forms/MobileClientPasswordResetForm.js";
import ShippingCostTable from "./views/Tables/ShippingCostTable.js";
import PartnerActivate from "./views/Pages/PartnerActivate.js";
import PartnerTwoFAVerify from "./views/Pages/PartnerTwoFAVerify.js";
import StaffTwoFAVerify from "./views/Pages/StaffTwoFAVerify.js";
import { publicRoutes } from "./routes.js";
import VendorQuoteRequestDetail from "./views/Publications/VendorQuoteRequestDetail.js";
import {
  LOGIN_ROUTE,
  resolveSessionLandingRoute,
} from "./utilities/landingRoute.js";

/**
 * The last rule in the Switch: whatever this URL was, it matched nothing.
 *
 * It used to be `<Redirect from="/" to="/auth/login-page" />`, which treated
 * "no route matched" as "nobody is signed in" and threw an authenticated
 * partner back to the login form for visiting "/". Now the question is asked
 * of the session rather than assumed: signed in goes to that account's own
 * dashboard, signed out goes to login.
 *
 * `replace` on both, so the unmatched URL does not sit in the history and
 * send the user straight back here on Back.
 */
const NoMatchRedirect = () => {
  const landing = resolveSessionLandingRoute();
  return <Redirect to={landing || LOGIN_ROUTE} />;
};

const App = () => {
  return (
    <Router>
      <>
      <Switch>
        {/* Public Routes */}
        {/* IMPORTANT: these must come BEFORE /auth to prevent the AuthLayout
            catch-all from intercepting them */}
        <Route path="/auth/partner-activate" component={PartnerActivate} />
        <Route path="/auth/partner-2fa" component={PartnerTwoFAVerify} />
        <Route path="/auth/staff-2fa" component={StaffTwoFAVerify} />
        <Route path="/auth" component={AuthLayout} />
        <Route path="/vendorRegistration" component={VendorRegistration} />
        {/* Seller-activation and brand-owner emails still link here. It redeems
            the same PUT /user/reset-password as /agent-reset-password, so it
            renders the same form: link check up front, live password rules,
            and a clear way forward from a dead link. */}
        <Route
          path="/reset-password/:token/:id"
          component={AgentPasswordResetForm}
        />
        <Route
          path="/agentPasswordReset-request"
          component={AgentPasswordResetRequestScreen}
        />
        <Route path="/vendor-register" component={VendorRegistrationForm} />
        <Route
          path="/agent-reset-password/:token/:id"
          component={AgentPasswordResetForm}
        />
        {/* 3PL staff invite/forgot-password redeem — same form, 3PL endpoint */}
        <Route
          path="/3pl-reset-password/:token/:id"
          render={(props) => (
            <AgentPasswordResetForm
              {...props}
              endpointBase="/api/3pl-staff/reset-password"
            />
          )}
        />
        <Route path="/mobileClient-reset-password/:token/:id"
          component={MobileClientPasswordResetForm}
        />
        <Route path="/shippingCost" component={ShippingCostTable} />
        {/* Vendor email deep-link: Respond with a quote */}
        <Route path="/vendor/quote-requests/:id" component={VendorQuoteRequestDetail} />
        {publicRoutes.map((route, index) => (
          <Route
            key={index}
            path={route.layout + route.path}
            component={route.component}
            exact={route.exact}
          />
        ))}

        {/* Private Routes */}
        <PrivateRoute path="/admin" component={AdminLayout} />

        {/* No route matched: hand the visitor to their own dashboard when
            they are signed in, and to login only when they are not. */}
        <Route component={NoMatchRedirect} />
      </Switch>
      {/* One COD cash receipt dialog for the whole dashboard, opened through
          utilities/codCashReceiptBus.js by Mark as Paid and the Riders page. */}
      <CodCashReceiptDialog />
      {/* One receipt dialog for the whole dashboard, opened through
          utilities/receiptDialogBus.js by every Print Receipt button. */}
      <ReceiptPrintDialog />
      </>
    </Router>
  );
};

export default App;
